import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Skeleton,
  alpha,
} from '@mui/material';
import TourIcon from '@mui/icons-material/Tour';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CategoryIcon from '@mui/icons-material/Category';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useAuth } from '../../context/AuthContext';
import API from '../../api/axios';

const StatCard = ({ icon, value, label, color, isLoading }) => (
  <Box
    sx={{
      p: 3,
      borderRadius: 3,
      bgcolor: alpha(color, 0.08),
      border: `1px solid ${alpha(color, 0.15)}`,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: 2.5,
        bgcolor: alpha(color, 0.15),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& .MuiSvgIcon-root': { fontSize: 26, color },
      }}
    >
      {icon}
    </Box>
    <Box>
      {isLoading ? (
        <Skeleton width={60} height={36} />
      ) : (
        <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1.2 }}>
          {value}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const menuItems = [
  {
    title: 'Manage Tours',
    description: 'Add, edit, or delete tour packages',
    icon: <TourIcon sx={{ fontSize: 32 }} />,
    path: '/admin/tours',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#667eea',
  },
  {
    title: 'Locations',
    description: 'Update branch locations and details',
    icon: <LocationOnIcon sx={{ fontSize: 32 }} />,
    path: '/admin/locations',
    gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: '#11998e',
  },
  {
    title: 'Services',
    description: 'Configure available services',
    icon: <MiscellaneousServicesIcon sx={{ fontSize: 32 }} />,
    path: '/admin/services',
    gradient: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    color: '#f7971e',
  },
  {
    title: 'Bookings',
    description: 'View and manage customer bookings',
    icon: <BookOnlineIcon sx={{ fontSize: 32 }} />,
    path: '/admin/bookings',
    gradient: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
    color: '#f5576c',
  },
  {
    title: 'Offers',
    description: 'Manage exclusive holiday offers',
    icon: <LocalOfferIcon sx={{ fontSize: 32 }} />,
    path: '/admin/offers',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#667eea',
  },
  {
    title: 'Inquiries',
    description: 'View and manage callback requests',
    icon: <ContactMailIcon sx={{ fontSize: 32 }} />,
    path: '/admin/inquiries',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
    color: '#f59e0b',
  },
  {
    title: 'Categories',
    description: 'Manage tour categories',
    icon: <CategoryIcon sx={{ fontSize: 32 }} />,
    path: '/admin/categories',
    gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
    color: '#10b981',
  },
  {
    title: 'Subscribers',
    description: 'View newsletter subscribers',
    icon: <MarkEmailReadIcon sx={{ fontSize: 32 }} />,
    path: '/admin/subscribers',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
    color: '#8b5cf6',
  },
];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ tours: 0, bookings: 0, locations: 0, services: 0, offers: 0, inquiries: 0, categories: 0, subscribers: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [toursRes, bookingsRes, locationsRes, servicesRes, offersRes, inquiriesRes, categoriesRes, subscribersRes] = await Promise.allSettled([
          API.get('/tours'),
          API.get('/bookings'),
          API.get('/locations'),
          API.get('/services'),
          API.get('/offers'),
          API.get('/inquiries'),
          API.get('/categories'),
          API.get('/subscribers'),
        ]);
        const toursData = toursRes.status === 'fulfilled' ? toursRes.value.data : [];
        setStats({
          tours: Array.isArray(toursData) ? toursData.length : (toursData.tours ? toursData.tours.length : 0),
          bookings: bookingsRes.status === 'fulfilled' ? bookingsRes.value.data.length : 0,
          locations: locationsRes.status === 'fulfilled' ? locationsRes.value.data.length : 0,
          services: servicesRes.status === 'fulfilled' ? servicesRes.value.data.length : 0,
          offers: offersRes.status === 'fulfilled' ? offersRes.value.data.length : 0,
          inquiries: inquiriesRes.status === 'fulfilled' ? inquiriesRes.value.data.length : 0,
          categories: categoriesRes.status === 'fulfilled' ? categoriesRes.value.data.length : 0,
          subscribers: subscribersRes.status === 'fulfilled' ? subscribersRes.value.data.length : 0,
        });
      } catch (e) {
        // silently fail
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      {/* Header Banner */}
      <Box
        sx={{
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: { xs: 3, md: 4 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -60,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.08)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -80,
            left: -30,
            width: 180,
            height: 180,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.05)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 0.5 }}>
            {getGreeting()}, {user?.name || 'Admin'} 👋
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>
            Here's what's happening with your travel agency today.
          </Typography>
        </Box>
        <TrendingUpIcon sx={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)', fontSize: 80, color: 'rgba(255,255,255,0.1)', display: { xs: 'none', md: 'block' } }} />
      </Box>

      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TourIcon />} value={stats.tours} label="Total Tours" color="#667eea" isLoading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<BookOnlineIcon />} value={stats.bookings} label="Total Bookings" color="#f5576c" isLoading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<LocalOfferIcon />} value={stats.offers} label="Exclusive Offers" color="#764ba2" isLoading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<ContactMailIcon />} value={stats.inquiries} label="Inquiries" color="#f59e0b" isLoading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<LocationOnIcon />} value={stats.locations} label="Locations" color="#11998e" isLoading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MiscellaneousServicesIcon />} value={stats.services} label="Services" color="#f7971e" isLoading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<CategoryIcon />} value={stats.categories} label="Categories" color="#10b981" isLoading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MarkEmailReadIcon />} value={stats.subscribers} label="Subscribers" color="#8b5cf6" isLoading={loadingStats} />
        </Grid>
      </Grid>

      {/* Quick Access Cards */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: 'text.primary' }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3}>
        {menuItems.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.title}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: `0 20px 50px ${alpha(item.color, 0.2)}`,
                  '& .card-arrow': { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <CardActionArea onClick={() => navigate(item.path)} sx={{ height: '100%', p: 0.5 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2.5,
                      background: item.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      boxShadow: `0 8px 20px ${alpha(item.color, 0.35)}`,
                      color: 'white',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.05rem' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {item.description}
                  </Typography>
                  <Box
                    className="card-arrow"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: item.color,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      opacity: 0,
                      transform: 'translateX(-8px)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Open <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;